/**
 * WebOrderTrackingViewModel
 * Web platform adapter for OrderTrackingViewModel
 * Provides React Router integration and web-specific services
 */

import { useNavigate, useLocation } from 'react-router-dom'
import {
  createOrderTrackingViewModel,
  createWebPlatformServices,
  WebOrderTrackingService,
  type OrderTrackingViewModelDependencies
} from '@restaurant/viewmodels'
import { OrderService, RestaurantService } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'

/**
 * Web adapter for OrderTrackingViewModel with React Router integration
 */
export const useOrderTrackingViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()
  
  // Create web-specific platform services
  const platformServices = createWebPlatformServices(navigate, location)
  
  // Create web-specific order tracking service
  const orderTrackingService = new WebOrderTrackingService(
    new OrderService({ apiClient }),
    new RestaurantService({ apiClient })
  )

  // Create dependencies with web services
  const dependencies: OrderTrackingViewModelDependencies = {
    orderTrackingService,
  }
  
  // Create and return the platform-agnostic OrderTrackingViewModel
  const OrderTrackingViewModelHook = createOrderTrackingViewModel(platformServices, dependencies)
  return OrderTrackingViewModelHook()
}

/**
 * Hook variant that accepts custom services for testing
 */
export const useOrderTrackingViewModelWithServices = (
  orderService?: OrderService,
  restaurantService?: RestaurantService
) => {
  const navigate = useNavigate()
  const location = useLocation()
  
  // Create web-specific platform services
  const platformServices = createWebPlatformServices(navigate, location)
  
  // Create web-specific order tracking service with custom services
  const orderTrackingService = new WebOrderTrackingService(
    orderService,
    restaurantService
  )

  // Create dependencies with custom services
  const dependencies: OrderTrackingViewModelDependencies = {
    orderTrackingService,
  }
  
  // Create and return the platform-agnostic OrderTrackingViewModel
  const OrderTrackingViewModelHook = createOrderTrackingViewModel(platformServices, dependencies)
  return OrderTrackingViewModelHook()
}